import { type Editor } from "@tiptap/react";
import SubmitIcon from "assets/img/icons/submit.svg?react";
import {
    AudioRecorder,
    Avatar,
    Button,
    FileAttachment,
    ProseDocument,
    SwitchInput,
} from "components/elements";
import { AttachFilesButton } from "components/form/AttachFilesButton";
import { PostEditor } from "components/form/PostEditor";
import { TemplatePickerDialog, resolveTemplate } from "components/form/TemplatePicker";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useUploadFiles } from "hooks/useUploadFiles";
import { cn } from "lib/cn";
import {
    type ApiThreadComment,
    type ThreadContext,
    formatAuthorName,
    getContextFeatures,
} from "lib/threadCommentUtils";
import { useCallback, useEffect, useRef, useState } from "react";
import type { TemplateContextValue } from "@compass/shared/schema/api/templates";
import type { TipTapDoc } from "@compass/shared/schema/tiptapDoc";
import { getPlainTextFromDoc } from "@compass/shared/tiptap/plaintext";
import { ClientDocument } from "@compass/shared/types";

type EditorFeatures = {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    strike?: boolean;
    heading?: boolean;
    blockquote?: boolean;
    bulletList?: boolean;
    orderedList?: boolean;
    indent?: boolean;
    redoUndo?: boolean;
    link?: boolean;
    table?: boolean;
};

export interface ThreadCommentFormProps {
    context: ThreadContext;
    mode?: "create" | "edit";
    /** TipTap doc to seed the editor in edit mode. */
    initialContentJson?: TipTapDoc | null;
    initialAttachments?: ClientDocument[];
    initialIsAnonymous?: boolean;
    replyingToComment?: ApiThreadComment | null;
    onSubmit: (data: {
        contentJson: TipTapDoc;
        attachmentIds: string[];
        isAnonymous: boolean;
    }) => void;
    onCancel?: () => void;
    isLoading?: boolean;
    placeholder?: string;
    submitLabel?: string;
    showCancel?: boolean;
    componentType?: string;
    editorFeatures?: EditorFeatures;
    /** Render the attachment control as a compact toolbar icon instead of a
     *  labelled button (used by the vendor composer). */
    compactAttachments?: boolean;
    showUserIdentity?: boolean;
    className?: string;
    toolbarBgColor?: string;
    contentHeightClassName?: string;
    editorRef?: React.Ref<Editor>;
    onEnter?: (e?: React.FormEvent<HTMLFormElement>) => void;
    clearFormRef?: React.MutableRefObject<(() => void) | null>;
    /** First non-Compass thread-participant org name, forwarded to the
     *  template picker as {{organization_name}}. Optional — empty when not
     *  passed. */
    templateOrganizationName?: string;
}

export const ThreadCommentForm = ({
    context,
    mode = "create",
    initialContentJson,
    initialAttachments,
    initialIsAnonymous,
    replyingToComment,
    onSubmit,
    onCancel,
    isLoading,
    placeholder,
    submitLabel = "Submit",
    showCancel = false,
    componentType,
    editorFeatures,
    compactAttachments = false,
    showUserIdentity = false,
    className,
    toolbarBgColor,
    contentHeightClassName,
    editorRef: externalEditorRef,
    onEnter,
    clearFormRef,
    templateOrganizationName,
}: ThreadCommentFormProps) => {
    const [editor, setEditor] = useState<Editor | null>(null);
    const [editorDoc, setEditorDoc] = useState<TipTapDoc | null>(null);
    const [isAnonymous, setIsAnonymous] = useState<boolean>(initialIsAnonymous ?? false);
    const [attachments, setAttachments] = useState<ClientDocument[]>(initialAttachments ?? []);

    const currentUser = useCurrentUser();
    const contextFeatures = getContextFeatures(context);

    // PRTL-1322: where uploads in this composer happen, for the admin upload
    // notification wording. Only "discussion" is private; the other thread
    // contexts (social / blog / vendor / multimedia) are public posts.
    const uploadNotifyContext = context === "discussion" ? "discussion" : "social";

    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);

    // "Start with a template" is offered to Compass users on discussion /
    // social composers. {{reply_to_name}} resolves to the comment being
    // replied to when present; selecting a template replaces the draft body.
    const templateKind: TemplateContextValue | null =
        context === "discussion" ? "PRIVATE" : context === "social" ? "SOCIAL" : null;
    const showTemplateButton = Boolean(templateKind && currentUser?.isCompassUser);

    const applyTemplate = (template: Parameters<typeof resolveTemplate>[0]) => {
        const { body } = resolveTemplate(template, {
            replyToName: replyingToComment ? formatAuthorName(replyingToComment.author) : undefined,
            organizationName: templateOrganizationName,
            userFirstName: currentUser?.firstName,
            userLastName: currentUser?.lastName,
        });
        editor?.commands.setContent(body as object, { emitUpdate: false });
        setEditorDoc(body);
    };
    const [isDragActive, setIsDragActive] = useState(false);
    const dragCounterRef = useRef(0);

    // PRTL-704 / PRTL-830 / PRTL-1125: upload File[] (from paste or drop) as
    // attachments. Context → bucket: "discussion" uploads as PRIVATE_ATTACHMENT
    // (same default as the detailed upload modal in discussion context);
    // everything else public. The picker paths live in AttachFilesButton, which
    // takes the same bucket.
    const attachmentUploadContext =
        context === "discussion" ? "PRIVATE_ATTACHMENT" : "PUBLIC_ATTACHMENT";
    const { uploadFiles, isUploading: isUploadingBatch } = useUploadFiles(
        attachmentUploadContext,
        uploadNotifyContext,
    );

    const uploadFilesAsAttachments = useCallback(
        async (files: File[]) => {
            const uploaded = await uploadFiles(files);
            if (uploaded.length > 0) {
                setAttachments((prev) => {
                    const existingIds = new Set(prev.map((a) => a.id));
                    return [...prev, ...uploaded.filter((f) => !existingIds.has(f.id))];
                });
            }
        },
        [uploadFiles],
    );

    const internalEditorRef = useCallback(
        (editorInstance: Editor | null) => {
            if (editorInstance) {
                setEditor(editorInstance);
            }
            // Forward to external ref
            if (externalEditorRef) {
                if (typeof externalEditorRef === "function") {
                    externalEditorRef(editorInstance);
                } else {
                    (externalEditorRef as React.MutableRefObject<Editor | null>).current =
                        editorInstance;
                }
            }
        },
        [externalEditorRef],
    );

    // Populate editor for edit mode
    useEffect(() => {
        if (mode === "edit" && initialContentJson && editor) {
            editor.commands.setContent(initialContentJson as object, { emitUpdate: false });
            setEditorDoc(initialContentJson);
        }
    }, [mode, initialContentJson, editor]);

    // Sync initial attachments when they change (edit mode)
    useEffect(() => {
        if (initialAttachments) {
            setAttachments(initialAttachments);
        }
    }, [initialAttachments]);

    // Sync initial anonymous state
    useEffect(() => {
        if (initialIsAnonymous !== undefined) {
            setIsAnonymous(initialIsAnonymous);
        }
    }, [initialIsAnonymous]);

    const clearForm = useCallback(() => {
        setEditorDoc(null);
        editor?.commands.clearContent(false);
        setIsAnonymous(false);
        setAttachments([]);
    }, [editor]);

    useEffect(() => {
        if (clearFormRef) {
            clearFormRef.current = clearForm;
        }
    }, [clearFormRef, clearForm]);

    const hasText = editorDoc ? getPlainTextFromDoc(editorDoc).trim().length > 0 : false;
    const hasContent = hasText || attachments.length > 0;

    const handleSubmit = () => {
        if (!hasContent || !editorDoc) return;
        onSubmit({
            contentJson: editorDoc,
            attachmentIds: attachments.map((a) => a.id),
            isAnonymous,
        });
    };

    const handleEnter = onEnter
        ? onEnter
        : (e?: React.FormEvent<HTMLFormElement>) => {
              handleSubmit();
          };

    const addAttachment = (file: ClientDocument) => {
        setAttachments((prev) => (prev.some((a) => a.id === file.id) ? prev : [...prev, file]));
    };

    const addAttachments = (files: ClientDocument[]) => {
        setAttachments((prev) => {
            const existingIds = new Set(prev.map((a) => a.id));
            const newFiles = files.filter((f) => !existingIds.has(f.id));
            return [...prev, ...newFiles];
        });
    };

    const removeAttachment = (file: ClientDocument) => {
        setAttachments((prev) => prev.filter((a) => a.id !== file.id));
    };

    const displayName = isAnonymous ? "Anonymous" : (currentUser?.name ?? "");
    const displayAvatarUrl = isAnonymous ? null : currentUser?.avatarUrl;

    const defaultPlaceholder =
        mode === "edit" ? "Edit your comment..." : "Write your comment here...";

    // Intercept paste of image files and upload as attachments (PRTL-704).
    // Pasted text/HTML (incl. Excel tables, which are HTML tables on the
    // clipboard) falls through to TipTap which already renders them via the
    // table extensions.
    const handlePaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
        const items = Array.from(e.clipboardData.items);
        const imageFiles: File[] = [];
        for (const item of items) {
            if (item.kind === "file" && item.type.startsWith("image/")) {
                const file = item.getAsFile();
                if (file) imageFiles.push(file);
            }
        }
        if (imageFiles.length === 0) return;
        e.preventDefault();
        e.stopPropagation();
        void uploadFilesAsAttachments(imageFiles);
    };

    // PRTL-830: drag-drop support. We nest-count to avoid the "flicker" where
    // dragenter/dragleave fire on child elements.
    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        if (!Array.from(e.dataTransfer.types).includes("Files")) return;
        e.preventDefault();
        dragCounterRef.current += 1;
        setIsDragActive(true);
    };
    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        if (!Array.from(e.dataTransfer.types).includes("Files")) return;
        e.preventDefault();
        dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
        if (dragCounterRef.current === 0) setIsDragActive(false);
    };
    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        if (!Array.from(e.dataTransfer.types).includes("Files")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
    };
    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        if (!Array.from(e.dataTransfer.types).includes("Files")) return;
        e.preventDefault();
        dragCounterRef.current = 0;
        setIsDragActive(false);
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) void uploadFilesAsAttachments(files);
    };

    return (
        <div
            className={cn(
                "relative flex flex-col",
                isDragActive && "ring-2 ring-blue-400 ring-offset-2",
                className,
            )}
            onPaste={handlePaste}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {(isDragActive || isUploadingBatch) && (
                <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-md bg-blue-50/80 text-sm font-medium text-blue-700">
                    {isUploadingBatch ? "Uploading…" : "Drop files to attach"}
                </div>
            )}
            {/* Reply preview */}
            {replyingToComment && (
                <div className="mb-3 rounded-md border border-gray-200 bg-gray-50 p-3">
                    <p className="mb-1 text-xs font-medium text-gray-500">
                        Replying to {replyingToComment.author.name}
                    </p>
                    <div className="line-clamp-3 text-sm text-gray-700">
                        <ProseDocument doc={replyingToComment.contentJson} />
                    </div>
                </div>
            )}

            {/* User identity row */}
            {showUserIdentity && (
                <div className="mb-3 flex items-center gap-2">
                    <Avatar
                        className="h-[40px] w-[40px] shrink-0 bg-slate-200 text-xs text-slate-400"
                        photoUrl={displayAvatarUrl}
                        name={displayName}
                    />
                    <div className="text-sm font-medium text-slate-900">{displayName}</div>
                    {contextFeatures.anonymous && (
                        <SwitchInput
                            name="anonymous"
                            value={isAnonymous}
                            label="Post anonymously"
                            onChange={setIsAnonymous}
                        />
                    )}
                </div>
            )}

            {showTemplateButton && templateKind && (
                <div className="mb-2 flex justify-end">
                    <Button
                        type="button"
                        variant="secondary"
                        className="text-xs"
                        onClick={() => setTemplateDialogOpen(true)}
                    >
                        Start with a template
                    </Button>
                    <TemplatePickerDialog
                        open={templateDialogOpen}
                        onOpenChange={setTemplateDialogOpen}
                        kind={templateKind}
                        onSelect={applyTemplate}
                    />
                </div>
            )}

            {/* Editor */}
            <PostEditor
                ref={internalEditorRef}
                onChange={(doc) => setEditorDoc(doc)}
                placeholder={placeholder ?? defaultPlaceholder}
                componentType={componentType}
                features={editorFeatures}
                contentHeightClassName={contentHeightClassName}
                toolbarBgColor={toolbarBgColor}
                onEnter={handleEnter}
                toolbarExtras={
                    compactAttachments ? (
                        <div className="flex items-center gap-1">
                            <AttachFilesButton
                                appearance="icon"
                                attachments={attachments}
                                onAdd={addAttachments}
                                uploadContext={attachmentUploadContext}
                                notifyContext={uploadNotifyContext}
                                showPrivateFiles={context === "discussion"}
                                defaultPublic={context !== "discussion"}
                            />
                            <AudioRecorder
                                onRecordingComplete={addAttachment}
                                isPublic={context !== "discussion"}
                                notifyContext={uploadNotifyContext}
                            />
                        </div>
                    ) : undefined
                }
            />

            {/* Anonymous toggle (when not in identity row) */}
            {!showUserIdentity && contextFeatures.anonymous && (
                <div className="mt-2">
                    <SwitchInput
                        name="anonymous"
                        value={isAnonymous}
                        label="Post anonymously"
                        onChange={setIsAnonymous}
                    />
                </div>
            )}

            {/* Attachments list */}
            {attachments.length > 0 && (
                <div className="mt-3 flex flex-col gap-2">
                    <div className="flex max-h-48 flex-col gap-2 overflow-y-auto">
                        {attachments.map((attachment) => (
                            <FileAttachment
                                key={attachment.id}
                                file={attachment}
                                onRemove={removeAttachment}
                                showAIDetails={false}
                                aiToggle={attachment.canManageAI}
                            />
                        ))}
                    </div>
                    {context !== "discussion" && (
                        <p className="text-xs text-amber-600">
                            Attached files will be visible to all users in this public section.
                            Other users will be able to download these files. Do not attach
                            sensitive or confidential content.
                        </p>
                    )}
                </div>
            )}

            {/* Attachment controls (text buttons). In compact mode (vendor composer)
                these move into the editor toolbar via `toolbarExtras` above. */}
            {!compactAttachments && (
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <AttachFilesButton
                        attachments={attachments}
                        onAdd={addAttachments}
                        uploadContext={attachmentUploadContext}
                        notifyContext={uploadNotifyContext}
                        showPrivateFiles={context === "discussion"}
                        defaultPublic={context !== "discussion"}
                    />
                    <AudioRecorder
                        onRecordingComplete={addAttachment}
                        isPublic={context !== "discussion"}
                        notifyContext={uploadNotifyContext}
                    />
                </div>
            )}

            {/* Actions */}
            <div className="mt-3 flex justify-end gap-2">
                {showCancel && onCancel && (
                    <Button type="button" variant="secondary" onClick={onCancel}>
                        Cancel
                    </Button>
                )}
                <Button
                    variant="primary"
                    isLoading={isLoading}
                    disabled={!hasContent}
                    onClick={handleSubmit}
                >
                    {submitLabel} <SubmitIcon className="ml-2" />
                </Button>
            </div>
        </div>
    );
};
